import {inject, Injectable} from '@angular/core';
import {
  BehaviorSubject, Observable, combineLatest, defer, of, switchMap, take, tap, catchError,
  map, shareReplay, distinctUntilChanged, filter, auditTime,
} from 'rxjs';
import {HttpErrorResponse} from '@angular/common/http';

import {CittadinoMensaStudenteDTO, CittadinoStudentiMensaService} from '../../../../core/api/generated';
import {GlobalManagerFigliService} from '../../../../services/global-manager-figli.service';
import {GlobalService} from '../../../../services/global.service';
import {LocalStorageService} from '../../../../services/local-storage.service';
import {resolveChildSelection, mensaRowKey, confrontaRigaStabile} from './resolve-child-selection';
import {mapDiscoveryToStudente} from './map-discovery-studente';
import {skipForbiddenRedirect} from '../../../../interceptors/forbidden-redirect.context';

/** Coppia di contesto mensa coerente: servizio (derivato dalla riga) + figlio. */
export interface ActiveMealContext {
  serviceId: string;
  childId: string;
}

/**
 * Holder LEGGERO della discovery studenti mensa CROSS-SERVIZIO (modello studente-first).
 *
 * Tiene la lista cross-servizio delle righe `(figlio, servizio)` + la riga selezionata +
 * la persistenza della selezione. È volutamente distinto da `GlobalManagerFigliService`
 * (sorgente pesante: portafogli, configurazioneComune, dieta…): qui solo i campi del DTO
 * leggero `CittadinoMensaStudenteDTO` (id, nome, cognome, scuola minimale, is_sospeso,
 * is_esente, servizio:{id, comune_*}). Vedi memoria [[mensa-discovery-due-livelli]].
 *
 * INVERSIONE del driver di contesto (design D3): la RIGA selezionata è il driver; l'header
 * `Gestione-Mense-Servizio-Id` deriva da `selezionato.servizio.id`, NON più da
 * `ServiziAttiviService`. La lista si carica UNA volta via `getMieiStudenti()` (senza header)
 * e contiene tutti i figli di tutti i comuni; lo stesso figlio in più comuni = più righe
 * (chiave `mensaRowKey = id::servizio.id`). `activeMealContext$` emette `{serviceId, childId}`
 * con `serviceId` preso dalla riga. Vedi [[mensa-passo-tre-flusso]].
 *
 * 404 e `200 []` collassano nello stesso empty privacy-preserving.
 */
@Injectable({providedIn: 'root'})
export class StudentiMensaService {
  private static readonly LS_KEY = 'STUDENTE_MENSA_ID';

  private readonly api = inject(CittadinoStudentiMensaService);
  private readonly managerFigli = inject(GlobalManagerFigliService);
  private readonly global = inject(GlobalService);
  private readonly localStorage = inject(LocalStorageService);

  constructor() {
    // Refresh del saldo CENTRALIZZATO (design D3): il saldo/residuo mostrato in home, header
    // e pagamenti arriva SEMPRE dalla discovery `studenti$`. Sui motivi del bus che toccano il
    // portafoglio (`evento-retroattivo`, `ricarica`) ricarichiamo la discovery qui, in un solo
    // punto, così una sola fetch aggiorna tutti i consumatori reattivi (nessun `carica$()` sparso
    // nelle pagine → niente fetch concorrenti). `auditTime` coalescia i burst ravvicinati (es.
    // range multi-giorno) e `switchMap` scarta le fetch obsolete (D4). `catchError` interno tiene
    // vivo lo stream: un errore di rete degrada a empty senza spegnere il bus. Il servizio è
    // `providedIn:'root'` (vita = app), quindi la sottoscrizione non va sganciata.
    this.global.mensaChanged$
      .pipe(
        filter((reason) => reason === 'evento-retroattivo' || reason === 'ricarica'),
        auditTime(150),
        switchMap(() => this.carica$().pipe(
          catchError(() => of([] as CittadinoMensaStudenteDTO[])),
        )),
      )
      .subscribe();
  }

  private readonly studentiSubject = new BehaviorSubject<CittadinoMensaStudenteDTO[] | undefined>(undefined);
  /**
   * Lista cross-servizio corrente. `undefined` = non ancora caricata; `[]` = empty (404 o
   * lista vuota). La PRIMA sottoscrizione innesca il caricamento lazy (`ensureCaricato`), così
   * la lista è popolata sia entrando dall'Hub sia atterrando direttamente su una pagina
   * mensa (menu/pagamenti/eventi) che monta il child-selector o consuma `activeMealContext$`.
   */
  readonly studenti$ = defer(() => {
    this.ensureCaricato();
    return this.studentiSubject;
  });

  /** Avvio del caricamento lazy: una sola fetch finché lo stato non viene azzerato. */
  private caricamentoAvviato = false;

  private readonly selezionatoSubject = new BehaviorSubject<CittadinoMensaStudenteDTO | null>(null);
  /** Riga `(figlio, servizio)` selezionata (globale per la mensa). */
  readonly selezionato$ = this.selezionatoSubject.asObservable();

  /**
   * Contesto mensa coerente: emette `{serviceId, childId}` dove `serviceId` deriva dalla
   * RIGA selezionata (`selezionato.servizio.id`) e `childId = selezionato.id`. Emette SOLO
   * quando esiste una riga selezionata appartenente alla lista corrente (invariante invertita,
   * design D3). Negli stati incoerenti/non pronti emette `null`, così le pagine non restano
   * bloccate in loading né osservano combinazioni intermedie.
   */
  readonly activeMealContext$: Observable<ActiveMealContext | null> = combineLatest([
    this.studenti$,
    this.selezionato$,
  ]).pipe(
    map(([studenti, selezionato]): ActiveMealContext | null => {
      const serviceId = selezionato?.servizio?.id;
      if (!studenti || !selezionato?.id || !serviceId) {
        return null;
      }
      // La riga selezionata deve appartenere alla lista cross-servizio corrente.
      const key = mensaRowKey(selezionato);
      const appartiene = studenti.some((s) => mensaRowKey(s) === key);
      return appartiene ? {serviceId, childId: selezionato.id} : null;
    }),
    distinctUntilChanged(
      (a, b) => a?.serviceId === b?.serviceId && a?.childId === b?.childId,
    ),
    shareReplay({bufferSize: 1, refCount: true}),
  );

  get studenti(): CittadinoMensaStudenteDTO[] | undefined {
    return this.studentiSubject.getValue();
  }

  get selezionato(): CittadinoMensaStudenteDTO | null {
    return this.selezionatoSubject.getValue();
  }

  /**
   * Imposta la lista discovery e riconcilia la selezione via resolver. Idempotente e
   * convergente: stessa lista → stessa selezione.
   */
  setStudenti(studenti: CittadinoMensaStudenteDTO[]): void {
    this.applicaDiscovery(studenti);
  }

  /**
   * Seleziona una riga e persiste la scelta. Sorgente UNICA della selezione mensa.
   * Cambiare riga verso un altro comune cambia anche il `serviceId` emesso da
   * `activeMealContext$` (cambio silenzioso, design D3).
   *
   * Ponte TRANSITORIO (one-way) verso `GlobalManagerFigliService.currentStudente`: i
   * consumatori pesanti (calendario, profilo-figli, contatti, ⌘K) leggono ancora la
   * selezione da lì. Mappiamo l'id sullo studente pesante già caricato nelle anagrafiche
   * locali. Vedi [[mensa-passo-tre-flusso]].
   */
  setSelezionato(riga: CittadinoMensaStudenteDTO | null): void {
    this.selezionatoSubject.next(riga);
    const key = mensaRowKey(riga);
    if (key) {
      this.salvaSelezione(key);
    }
    this.sincronizzaSelezioneLegacy(riga);
  }

  /**
   * Seleziona il figlio con l'id indicato, se è fra quelli dell'utente.
   *
   * Serve ai collegamenti che arrivano da fuori — oggi il tocco su una notifica push, che porta
   * l'id dello studente a cui l'avviso si riferisce. Non è una scorciatoia per `setSelezionato`:
   * quello si fida della riga che riceve, questo accetta un id di provenienza esterna e lo
   * verifica contro la lista dell'utente.
   *
   * Un id sconosciuto **non** è un errore e non cambia nulla: la pagina resta sul figlio già
   * selezionato. È il caso normale di una notifica vecchia, aperta quando quel figlio non è più
   * iscritto — meglio atterrare sulla pagina giusta col figlio sbagliato che su una schermata di
   * errore. Il controllo vero su chi può vedere cosa resta comunque al backend, a ogni chiamata.
   *
   * @param idStudente id della riga da selezionare
   * @returns `true` se la selezione è stata applicata
   */
  selezionaPerId(idStudente: string | null | undefined): boolean {
    const id = (idStudente ?? '').trim();
    if (!id) {
      return false;
    }
    const riga = (this.studenti ?? []).find((s) => s.id === id);
    if (!riga) {
      return false;
    }
    if (this.selezionato?.id === id) {
      return true;
    }
    this.setSelezionato(riga);
    return true;
  }

  /**
   * Applica una lista discovery e riconcilia la selezione via resolver puro: la selezione
   * persistita/corrente è solo un suggerimento, mai applicata senza validarla contro
   * `children` correnti (invariante D6). Ordina le righe in modo stabile così che le righe
   * dello stesso figlio risultino adiacenti.
   */
  private applicaDiscovery(studenti: CittadinoMensaStudenteDTO[]): void {
    const ordinati = [...studenti].sort(confrontaRigaStabile);
    this.studentiSubject.next(ordinati);

    const {rowKey} = resolveChildSelection({
      serviceId: '',
      children: ordinati,
      currentRowKey: mensaRowKey(this.selezionato) || this.leggiSelezioneSalvata() || undefined,
    });

    this.setSelezionato(rowKey ? ordinati.find((s) => mensaRowKey(s) === rowKey) ?? null : null);
  }

  /**
   * Allinea la selezione legacy (`GlobalManagerFigliService.currentStudente`) all'id selezionato.
   * Sorgente = la RIGA discovery selezionata mappata via `mapDiscoveryToStudente`: il vecchio
   * lookup in `opzioniStudenti` è morto (array mai più popolato), quindi avrebbe sempre fallito
   * lasciando il ponte vuoto. Mappando direttamente dal DTO il ponte resta alimentato per i
   * consumatori pesanti (calendario, profilo-figli, redirect-ok). Vedi change
   * `repoint-command-palette-discovery` (decisione D5).
   */
  private sincronizzaSelezioneLegacy(riga: CittadinoMensaStudenteDTO | null): void {
    if (!riga?.id) {
      return;
    }
    if (this.managerFigli.currentStudente?.id !== riga.id) {
      this.managerFigli.setCurrentStudente(mapDiscoveryToStudente(riga));
    }
  }

  /** Azzera lo stato: lista non caricata + nessuna selezione; il prossimo accesso ricarica. */
  reset(): void {
    this.caricamentoAvviato = false;
    this.studentiSubject.next(undefined);
    this.setSelezionato(null);
  }

  /**
   * Carica la discovery cross-servizio e popola lo stato. Si usa all'avvio dell'Hub e su
   * refresh esplicito; è idempotente e converge allo stesso stato (stessa lista → stesso
   * resolver → stessa selezione). 404 e `200 []` → empty neutro.
   */
  carica$(): Observable<CittadinoMensaStudenteDTO[]> {
    // Guard anti-401 (vedi ensureCaricato): senza token non interroghiamo l'endpoint
    // autenticato. Degrada a empty neutro senza marcare `caricamentoAvviato`.
    if (!this.localStorage.getAccessToken()) {
      this.applicaDiscovery([]);
      return of([] as CittadinoMensaStudenteDTO[]);
    }
    this.caricamentoAvviato = true;
    return this.fetchStudenti$().pipe(
      take(1),
      tap((studenti) => this.applicaDiscovery(studenti)),
    );
  }

  /**
   * Innesca il caricamento lazy una sola volta. Idempotente: chiamato dalla prima
   * sottoscrizione a `studenti$`/`activeMealContext$` (qualsiasi pagina mensa) e dall'Hub.
   * Le sottoscrizioni successive trovano lo stato già popolato (o in caricamento).
   */
  private ensureCaricato(): void {
    if (this.caricamentoAvviato) {
      return;
    }
    // Guard anti-401: la discovery cittadino (`/api/v1/cittadino/servizi/mensa-studenti`)
    // richiede autenticazione. Da quando la pagina "nuova pratica" e' pubblica, la shell
    // `/pages/*` (e i consumatori globali: ⌘K, blocco-studenti home) puo' montarsi da
    // anonimo e sottoscrivere `studenti$` → senza token la fetch partirebbe senza
    // Authorization e tornerebbe 401. Senza token degradiamo a empty neutro (come per il
    // 404 gate/`200 []`), senza marcare `caricamentoAvviato`: al login successivo la
    // prossima sottoscrizione ricarica.
    if (!this.localStorage.getAccessToken()) {
      this.studentiSubject.next([]);
      return;
    }
    this.caricamentoAvviato = true;
    this.fetchStudenti$()
      .pipe(take(1))
      .subscribe((studenti) => this.applicaDiscovery(studenti));
  }

  /**
   * Fetch della discovery cross-servizio (`getMieiStudenti`, NESSUN header), con empty
   * privacy-preserving. 404 (gate: nessuna Pratica accettata) → `[]`. Errore non-404 →
   * degrado a `[]` per non bloccare la UI in loading perenne. `switchMap` lato chiamante
   * scarta le risposte obsolete su refresh ripetuti.
   *
   * `skipForbiddenRedirect()`: il 403 qui è **atteso e già gestito** dal `catchError` sotto
   * (degrado a `[]`), ma l'interceptor gira PRIMA del chiamante e redirigerebbe comunque a
   * `/pages/home`. Per un operatore di backoffice — che non è un cittadino, quindi prende 403
   * certo su questo endpoint CITTADINO-only — quel redirect lo espelle dall'area operatore
   * appena arrivato, e la home senza comune lo rimbalza su `scegli-comune`: la feature diventa
   * irraggiungibile. Chi gestisce l'errore in proprio deve dichiararlo.
   */
  private fetchStudenti$(): Observable<CittadinoMensaStudenteDTO[]> {
    return this.api.listMensaStudentiCittadino('body', false, {context: skipForbiddenRedirect()}).pipe(
      switchMap((studenti) => of(studenti ?? [])),
      catchError((err: HttpErrorResponse) => {
        if (err.status !== 404) {
          console.warn('[studenti-mensa] discovery cross-servizio fallita, degrado a empty', err?.status);
        }
        return of([] as CittadinoMensaStudenteDTO[]);
      }),
    );
  }

  private leggiSelezioneSalvata(): string | null {
    try {
      return localStorage.getItem(StudentiMensaService.LS_KEY);
    } catch {
      return null;
    }
  }

  private salvaSelezione(rowKey: string): void {
    try {
      localStorage.setItem(StudentiMensaService.LS_KEY, rowKey);
    } catch {
      // storage non disponibile: la selezione resta solo in memoria
    }
  }
}
